import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Loader2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { api, extractError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { useAuth } from "../context/AuthContext";

const ALL_ROLES = [
    { value: "platform_admin", label: "Platform Admin" },
    { value: "reviewer", label: "Reviewer" },
    { value: "org_leader", label: "Org Leader" },
    { value: "coach", label: "Coach" },
    { value: "teacher", label: "Teacher" },
];

function inviteUrlFor(inv) {
    if (inv?.invite_url && inv.invite_url.startsWith("http")) return inv.invite_url;
    return `${window.location.origin}/accept-invitation/${inv?.token}`;
}

export default function AdminInviteNewPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const isPlatform = user?.role === "platform_admin";

    const allowedRoles = isPlatform
        ? ALL_ROLES
        : ALL_ROLES.filter((r) => r.value !== "platform_admin");

    const [email, setEmail] = useState("");
    const [role, setRole] = useState(allowedRoles[0]?.value || "");
    const [fullName, setFullName] = useState("");
    const [orgId, setOrgId] = useState(user?.role === "org_leader" ? user.org_id : "");
    const [schoolId, setSchoolId] = useState("");
    const [orgs, setOrgs] = useState([]);
    const [schools, setSchools] = useState([]);
    const [loadingOrgs, setLoadingOrgs] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [createdInv, setCreatedInv] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!isPlatform) return;
        setLoadingOrgs(true);
        api.get("/admin/organizations")
            .then((r) => setOrgs(r.data))
            .catch((e) => toast.error(extractError(e, "Could not load orgs")))
            .finally(() => setLoadingOrgs(false));
    }, [isPlatform]);

    useEffect(() => {
        setSchools([]);
        setSchoolId("");
        if (!orgId) return;
        api.get(`/admin/organizations/${orgId}/schools`)
            .then((r) => setSchools(r.data))
            .catch((e) => toast.error(extractError(e, "Could not load schools")));
    }, [orgId]);

    const orgRequired = ["org_leader", "coach", "teacher"].includes(role);
    const schoolRequired = role === "teacher";

    const handleSubmit = async (e) => {
        e.preventDefault();
        setCreatedInv(null);
        if (orgRequired && !orgId) {
            toast.error("This role requires an organization.");
            return;
        }
        if (schoolRequired && !schoolId) {
            toast.error("Teacher role requires a school.");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/admin/invitations", {
                email: email.trim().toLowerCase(),
                role,
                org_id: orgRequired ? orgId : null,
                school_id: schoolRequired ? schoolId : null,
                full_name: fullName.trim() || undefined,
            });
            setCreatedInv(data);
            toast.success("Invitation created. Share the link below.");
            setEmail("");
            setFullName("");
        } catch (err) {
            toast.error(extractError(err, "Could not create invitation"));
        } finally {
            setSubmitting(false);
        }
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(inviteUrlFor(createdInv));
            setCopied(true);
            toast.success("Invitation URL copied");
            setTimeout(() => setCopied(false), 2000);
        } catch (e) {
            toast.error("Copy failed. Select the URL manually.");
        }
    };

    return (
        <div className="space-y-6 max-w-2xl" data-testid="admin-invite-new-page">
            <div>
                <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-2">
                    Invitations
                </div>
                <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-[#0B2545] leading-tight">
                    Invite a user
                </h1>
                <p className="mt-2 text-sm text-[#0E1B2C]/70">
                    Invitation-only. The user will receive a link to activate their account.
                </p>
            </div>

            <form
                onSubmit={handleSubmit}
                className="rounded-2xl bg-white border border-[#D6E6F2] p-6 space-y-5"
                data-testid="invite-form"
            >
                <div className="space-y-2">
                    <Label htmlFor="invite-email">Email</Label>
                    <Input
                        id="invite-email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="user@example.com"
                        data-testid="invite-email-input"
                    />
                </div>

                <div className="space-y-2">
                    <Label htmlFor="invite-name">Full name (optional)</Label>
                    <Input
                        id="invite-name"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="Rita Ortiz"
                        data-testid="invite-name-input"
                    />
                </div>

                <div className="space-y-2">
                    <Label>Role</Label>
                    <Select value={role} onValueChange={setRole}>
                        <SelectTrigger data-testid="invite-role-select">
                            <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                            {allowedRoles.map((r) => (
                                <SelectItem key={r.value} value={r.value} data-testid={`role-option-${r.value}`}>
                                    {r.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {orgRequired && (
                    <div className="space-y-2">
                        <Label>Organization {orgRequired && <span className="text-red-600">*</span>}</Label>
                        {isPlatform ? (
                            <Select
                                value={orgId || ""}
                                onValueChange={setOrgId}
                                disabled={loadingOrgs}
                            >
                                <SelectTrigger data-testid="invite-org-select">
                                    <SelectValue
                                        placeholder={loadingOrgs ? "Loading…" : "Select organization"}
                                    />
                                </SelectTrigger>
                                <SelectContent>
                                    {orgs.map((o) => (
                                        <SelectItem key={o.id} value={o.id} data-testid={`org-option-${o.slug}`}>
                                            {o.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        ) : (
                            <Input
                                value={user?.organization?.name || ""}
                                disabled
                                className="bg-[#D6E6F2]/50"
                                data-testid="invite-org-locked"
                            />
                        )}
                    </div>
                )}

                {schoolRequired && orgId && (
                    <div className="space-y-2">
                        <Label>
                            School <span className="text-red-600">*</span>
                        </Label>
                        <Select value={schoolId || ""} onValueChange={setSchoolId}>
                            <SelectTrigger data-testid="invite-school-select">
                                <SelectValue placeholder="Select school" />
                            </SelectTrigger>
                            <SelectContent>
                                {schools.map((s) => (
                                    <SelectItem key={s.id} value={s.id} data-testid={`school-option-${s.id}`}>
                                        {s.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}

                <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-11 rounded-full bg-[#0B2545] hover:bg-[#1E56A0] text-white font-semibold"
                    data-testid="invite-submit-btn"
                >
                    {submitting ? (
                        <>
                            <Loader2 className="animate-spin" size={16} />
                            Creating invitation…
                        </>
                    ) : (
                        <>
                            <Mail size={16} />
                            Create invitation
                        </>
                    )}
                </Button>
            </form>

            {createdInv && (
                <div
                    className="rounded-2xl bg-[#0B2545] text-white p-6 space-y-3"
                    data-testid="invite-result"
                >
                    <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8AC4FF]">
                        Invitation created
                    </div>
                    <div className="text-white/85 text-sm">
                        Share this URL with <strong>{createdInv.email}</strong>. It expires{" "}
                        {new Date(createdInv.expires_at).toLocaleDateString()}.
                    </div>
                    <div className="rounded-lg bg-white/10 border border-white/15 p-3 font-mono text-xs break-all" data-testid="invite-result-url">
                        {inviteUrlFor(createdInv)}
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={copy}
                            className="rounded-full bg-white text-[#0B2545] border-white hover:bg-[#D6E6F2]"
                            data-testid="invite-result-copy-btn"
                        >
                            {copied ? <Check size={14} /> : <Copy size={14} />}
                            {copied ? "Copied" : "Copy link"}
                        </Button>
                        {createdInv.org_id && (
                            <Button
                                variant="outline"
                                onClick={() =>
                                    navigate(`/app/admin/organizations/${createdInv.org_id}`)
                                }
                                className="rounded-full bg-transparent border-white/40 text-white hover:bg-white/10"
                                data-testid="invite-result-org-btn"
                            >
                                Open organization
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
